"use client";

import {
  ArrowDown,
  ArrowUp,
  Copy,
  Edit3,
  ExternalLink,
  Eye,
  ImageOff,
  Layers,
  Loader2,
  Megaphone,
  Plus,
  Tag,
  Trash2,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DEFAULT_CATEGORY_TREE, getCategoryPermanentLink } from "@/lib/categories";
import { cn } from "@/lib/utils";
import { apiDelete, apiPatch, apiPost } from "@/services/api";
import type { Promotion } from "@/types";

const EMPTY_FORM = {
  eyebrow: "",
  title: "",
  description: "",
  image: "",
  cta: "Shop now",
  link: "/search",
  targetType: "category" as "category" | "collection",
  target: "",
  isActive: true,
  sortOrder: "0",
};

export function PromotionsManager({
  initialPromotions,
  isLoading = false,
  onRefresh,
}: {
  initialPromotions: Promotion[];
  isLoading?: boolean;
  onRefresh?: () => void;
}) {
  const [promotions, setPromotions] = useState<Promotion[]>(initialPromotions);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  useEffect(() => {
    let active = true;
    async function loadCats() {
      try {
        const res = await fetch("/api/categories");
        if (res.ok) {
          const data = await res.json();
          if (active && Array.isArray(data.categories)) {
            setCategories(data.categories);
          }
        }
      } catch {
        // fallback to DEFAULT_CATEGORY_TREE
      }
    }
    void loadCats();
    return () => {
      active = false;
    };
  }, []);

  const allCategoryOptions = useMemo(() => {
    const list: { group: string; options: string[] }[] = [];
    const seen = new Set<string>();

    for (const [parent, subs] of Object.entries(DEFAULT_CATEGORY_TREE)) {
      const groupSubs: string[] = [parent];
      for (const s of subs) {
        if (!groupSubs.includes(s)) groupSubs.push(s);
      }
      list.push({ group: parent, options: groupSubs });
      groupSubs.forEach((c) => seen.add(c.toLowerCase()));
    }

    const extra: string[] = [];
    for (const cat of categories) {
      if (cat && !seen.has(cat.toLowerCase())) {
        seen.add(cat.toLowerCase());
        extra.push(cat);
      }
    }
    if (extra.length > 0) {
      list.push({ group: "Additional Store Categories", options: extra });
    }

    return list;
  }, [categories]);

  useEffect(() => {
    setPromotions(initialPromotions);
  }, [initialPromotions]);

  useEffect(() => {
    if (categoryParam) {
      setForm((prev) => ({
        ...prev,
        targetType: "category",
        target: categoryParam,
        link: `/search?category=${encodeURIComponent(categoryParam)}`,
      }));
      formRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [categoryParam]);

  function startEdit(promo: Promotion) {
    setEditing(promo);
    setImageError(false);
    setErrors({});

    const initialTargetType: "category" | "collection" =
      promo.targetType === "collection" || promo.link?.includes("collection=")
        ? "collection"
        : "category";

    let extractedTarget = promo.target || "";
    if (!extractedTarget && promo.link) {
      const match = promo.link.match(/[?&](?:category|collection)=([^&]+)/);
      if (match) extractedTarget = decodeURIComponent(match[1]);
    }

    setForm({
      eyebrow: promo.eyebrow,
      title: promo.title,
      description: promo.description,
      image: promo.image,
      cta: promo.cta || "Shop now",
      link: promo.link || "",
      targetType: initialTargetType,
      target: extractedTarget,
      isActive: promo.isActive,
      sortOrder: String(promo.sortOrder ?? 0),
    });
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function cancelEdit() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setImageError(false);
  }

  function setField(key: keyof typeof EMPTY_FORM, value: string | boolean) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function selectCategory(cat: string) {
    setField("targetType", "category");
    setField("target", cat);
    if (cat) {
      setField("link", `/search?category=${encodeURIComponent(cat)}`);
    } else {
      setField("link", "/search");
    }
  }

  function selectCollection(col: string) {
    setField("targetType", "collection");
    setField("target", col);
    if (col) {
      setField("link", `/search?collection=${encodeURIComponent(col)}`);
    } else {
      setField("link", "/search");
    }
  }

  function validate() {
    const errs: Record<string, string> = {};
    if (!form.eyebrow.trim()) errs.eyebrow = "Eyebrow tag is required";
    if (!form.title.trim() || form.title.trim().length < 3)
      errs.title = "Title must be at least 3 characters";
    if (!form.description.trim()) errs.description = "Description is required";
    if (!form.image.trim()) errs.image = "Image URL is required";
    if (!form.cta.trim()) errs.cta = "Button text is required";
    if (!form.link.trim()) errs.link = "Link URL is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required promotion fields.");
      return;
    }

    setLoading(true);
    const payload = {
      eyebrow: form.eyebrow.trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      image: form.image.trim(),
      cta: form.cta.trim(),
      link: form.link.trim(),
      targetType: form.targetType,
      target: form.target.trim() || undefined,
      isActive: form.isActive,
      sortOrder: Number(form.sortOrder) || 0,
    };

    try {
      const url = editing ? `/api/admin/promotions/${editing.id}` : "/api/admin/promotions";
      const data = editing
        ? await apiPatch<{ promotion: Promotion }>(url, payload)
        : await apiPost<{ promotion: Promotion }>(url, payload);

      const saved: Promotion = data.promotion;
      setPromotions((prev) =>
        editing
          ? prev.map((p) => (p.id === editing.id ? saved : p))
          : [...prev, saved].sort((a, b) => a.sortOrder - b.sortOrder)
      );

      cancelEdit();
      onRefresh?.();
      toast.success(editing ? "Promotion updated" : "Promotion created", {
        description: "Visible on the storefront hero carousel immediately.",
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unable to save promotion");
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive(promo: Promotion) {
    const updatedStatus = !promo.isActive;
    const previous = promotions;
    setPromotions((prev) =>
      prev.map((p) => (p.id === promo.id ? { ...p, isActive: updatedStatus } : p))
    );

    try {
      await apiPatch(`/api/admin/promotions/${promo.id}`, { isActive: updatedStatus });
      onRefresh?.();
      toast.success(updatedStatus ? "Promotion activated" : "Promotion paused");
    } catch {
      setPromotions(previous);
      toast.error("Unable to update status");
    }
  }

  async function deletePromotion(id: string) {
    const previous = promotions;
    setPromotions((prev) => prev.filter((p) => p.id !== id));
    setConfirmDeleteId(null);

    try {
      await apiDelete(`/api/admin/promotions/${id}`);
      onRefresh?.();
      toast.success("Promotion banner removed");
    } catch {
      setPromotions(previous);
      toast.error("Unable to delete promotion");
    }
  }

  async function moveOrder(promo: Promotion, direction: "up" | "down") {
    const idx = promotions.findIndex((p) => p.id === promo.id);
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === promotions.length - 1) return;

    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    const otherPromo = promotions[targetIdx];

    const newPromotions = [...promotions];
    newPromotions[idx] = { ...otherPromo, sortOrder: promo.sortOrder };
    newPromotions[targetIdx] = { ...promo, sortOrder: otherPromo.sortOrder };

    setPromotions(newPromotions);

    try {
      await Promise.all([
        apiPatch(`/api/admin/promotions/${promo.id}`, { sortOrder: otherPromo.sortOrder }),
        apiPatch(`/api/admin/promotions/${otherPromo.id}`, { sortOrder: promo.sortOrder }),
      ]);
      onRefresh?.();
      toast.success("Order updated");
    } catch {
      setPromotions(promotions);
      toast.error("Failed to reorder promotions");
    }
  }

  const activeCount = useMemo(() => promotions.filter((p) => p.isActive).length, [promotions]);

  return (
    <div className="space-y-6">
      {/* Header Info Banner */}
      <div className="flex flex-col justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amazon-orange text-slate-950 shadow-xs">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-950 dark:text-white">
                Promotions & Hero Banners
              </h1>
              <p className="text-xs text-slate-500">
                Manage the main promotional banner carousel on the storefront homepage.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-center text-xs dark:border-white/10 dark:bg-slate-800">
            <span className="font-bold text-slate-950 dark:text-white">{promotions.length}</span>{" "}
            total ({activeCount} active)
          </div>
          <Link
            href="/"
            target="_blank"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 shadow-xs hover:border-amazon-orange hover:text-amazon-orange dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
          >
            <Eye className="h-3.5 w-3.5" />
            View Storefront
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </Link>
        </div>
      </div>

      {/* Live Card Preview & Sequence Strip */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-white/10 dark:bg-slate-900">
        <div className="flex flex-col justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-white/10 dark:bg-slate-800/60 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              Live Storefront Carousel Preview
            </h3>
            <p className="text-[11px] text-slate-500">
              Displays how promotional cards appear in the horizontal carousel on the homepage.
            </p>
          </div>
          <span className="text-[11px] font-medium text-slate-500">
            {activeCount} active in carousel
          </span>
        </div>

        <div className="p-4 sm:p-6 bg-[#eaeded] dark:bg-slate-950">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-6">
            {/* Card Preview */}
            <div
              className="group relative shrink-0 overflow-hidden rounded-[18px] bg-white shadow-md transition-all dark:bg-slate-900 border border-slate-200/80 dark:border-white/10"
              style={{ width: "215px", aspectRatio: "196 / 316" }}
            >
              {form.image && !imageError ? (
                <div className="relative h-full w-full">
                  <Image
                    src={form.image}
                    alt=""
                    fill
                    sizes="215px"
                    className="object-cover"
                    onError={() => setImageError(true)}
                  />
                  <div className="absolute inset-0 flex flex-col justify-between p-4 bg-gradient-to-b from-black/60 via-transparent to-black/60 text-white">
                    <h4 className="text-lg font-black leading-tight drop-shadow-sm">
                      {form.title || "Promotion Title"}
                    </h4>
                    {form.cta && (
                      <span className="inline-flex self-start rounded-full bg-amazon-gold px-3 py-1 text-xs font-bold text-slate-950 shadow">
                        {form.cta}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center p-4 text-center text-slate-400">
                  <ImageOff className="h-8 w-8 mb-2 text-slate-300" />
                  <p className="text-xs font-bold">Image Preview</p>
                  <p className="text-[11px]">Enter an image URL above</p>
                </div>
              )}
            </div>

            {/* Live Card Metadata Card */}
            <div className="flex-1 max-w-lg space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-amazon-orange">
                  {form.eyebrow || "Category / Eyebrow"}
                </span>
                <Badge tone={form.isActive ? "success" : "default"}>
                  {form.isActive ? "Active (In Carousel)" : "Paused"}
                </Badge>
              </div>

              <h4 className="text-base font-bold text-slate-950 dark:text-white">
                {form.title || "Your Promotional Card Title"}
              </h4>

              <p className="text-xs text-slate-600 dark:text-slate-400">
                {form.description || "Promotion description will appear in search keywords and admin views."}
              </p>

              <div className="pt-2 border-t border-slate-100 dark:border-white/10 flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span className="font-semibold text-slate-700 dark:text-slate-300">Destination:</span>
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] dark:bg-slate-800">
                    {form.link || "Not set"}
                  </code>
                </div>

                {form.link && (
                  <Link
                    href={form.link}
                    target="_blank"
                    className="inline-flex items-center gap-1 font-bold text-amazon-teal hover:underline text-xs"
                  >
                    Test Link
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Active Carousel Sequence Strip */}
          {promotions.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200/80 dark:border-white/10">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">
                Active Storefront Sequence (Click any card to edit)
              </p>
              <div className="flex gap-2.5 overflow-x-auto pb-1 no-scrollbar">
                {promotions.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => startEdit(p)}
                    className={`group relative shrink-0 overflow-hidden rounded-xl border text-left transition hover:scale-105 ${
                      editing?.id === p.id
                        ? "border-amazon-orange ring-2 ring-amazon-orange shadow-md"
                        : "border-slate-300/80 bg-white dark:border-white/10 dark:bg-slate-900"
                    }`}
                    style={{ width: "95px", aspectRatio: "196 / 316" }}
                    title={`#${i + 1}: ${p.title}`}
                  >
                    <Image
                      src={p.image}
                      alt={p.title}
                      fill
                      sizes="95px"
                      className="object-cover"
                    />
                    <div className="absolute top-1 left-1 rounded bg-black/75 px-1 py-0.5 text-[9px] font-black text-white">
                      #{i + 1}
                    </div>
                    {!p.isActive && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-[10px] font-black text-amber-300">
                        PAUSED
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Form */}
      <form
        ref={formRef}
        onSubmit={onSubmit}
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900"
      >
        <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3 dark:border-white/10">
          <div>
            <h2 className="text-lg font-black text-slate-950 dark:text-white">
              {editing ? "Edit Promotion Banner" : "Create New Promotion Banner"}
            </h2>
            <p className="text-xs text-slate-500">
              Customize text, banner image URL, and call-to-action button.
            </p>
          </div>
          {editing && (
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs font-bold text-amazon-teal hover:underline"
            >
              Cancel edit
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Eyebrow */}
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-800 dark:text-slate-200">
              Eyebrow Tag <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.eyebrow}
              onChange={(e) => setField("eyebrow", e.target.value)}
              placeholder="e.g. MOVA GREAT ETHIOPIAN SALE / MEGA ELECTRONICS SALE"
              className="h-10 w-full rounded-md border border-slate-200 bg-transparent px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
            />
            {errors.eyebrow && <p className="mt-1 text-xs text-red-500">{errors.eyebrow}</p>}
          </div>

          {/* Title */}
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-800 dark:text-slate-200">
              Banner Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="e.g. Up to 50% off top headphones and smart devices"
              className="h-10 w-full rounded-md border border-slate-200 bg-transparent px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
            />
            {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
          </div>

          {/* Image URL */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-bold text-slate-800 dark:text-slate-200">
              Banner Image URL <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={form.image}
                onChange={(e) => {
                  setField("image", e.target.value);
                  setImageError(false);
                }}
                placeholder="https://... or /hero/1.jpg"
                className="h-10 flex-1 rounded-md border border-slate-200 bg-transparent px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
              />
              <div className="relative h-10 w-16 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-slate-800">
                {form.image && !imageError ? (
                  <Image
                    src={form.image}
                    alt=""
                    fill
                    className="object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <ImageOff className="m-auto mt-2.5 h-4 w-4 text-slate-300" />
                )}
              </div>
            </div>
            {errors.image && <p className="mt-1 text-xs text-red-500">{errors.image}</p>}
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-bold text-slate-800 dark:text-slate-200">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="e.g. Curated deals on electronics, appliances, and everyday essentials with fast Prime delivery."
              className="w-full rounded-md border border-slate-200 bg-transparent p-2.5 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
            />
            {errors.description && (
              <p className="mt-1 text-xs text-red-500">{errors.description}</p>
            )}
          </div>

          {/* Target Product Filtering (Category vs Collection) */}
          <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-slate-800/50">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#073B70] dark:text-sky-300">
                  Target Product Filter <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Choose whether customer clicks on this banner filter products by store category or curated collection.
                </p>
              </div>

              {/* Type Switcher Pills */}
              <div className="flex rounded-lg border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-slate-900 shadow-2xs">
                <button
                  type="button"
                  onClick={() => {
                    const newTarget = form.targetType === "category" ? form.target : "";
                    setField("targetType", "category");
                    if (newTarget) {
                      setField("link", `/search?category=${encodeURIComponent(newTarget)}`);
                    }
                  }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-bold transition-all",
                    form.targetType === "category"
                      ? "bg-[#073B70] text-white shadow-2xs"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
                  )}
                >
                  <Tag className="h-3.5 w-3.5" />
                  Target by Category
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newTarget = form.targetType === "collection" ? form.target : "";
                    setField("targetType", "collection");
                    if (newTarget) {
                      setField("link", `/search?collection=${encodeURIComponent(newTarget)}`);
                    }
                  }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-bold transition-all",
                    form.targetType === "collection"
                      ? "bg-purple-700 text-white shadow-2xs"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
                  )}
                >
                  <Layers className="h-3.5 w-3.5" />
                  Target by Collection
                </button>
              </div>
            </div>

            {form.targetType === "category" ? (
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Select Store Category / Subcategory
                </label>
                <select
                  value={form.target}
                  onChange={(e) => selectCategory(e.target.value)}
                  className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-medium outline-none focus:border-amazon-orange dark:border-white/15 dark:bg-slate-900 dark:text-white"
                >
                  <option value="">-- Select Target Category or Subcategory --</option>
                  {allCategoryOptions.map((group) => (
                    <optgroup key={group.group} label={group.group}>
                      {group.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Selecting a subcategory (e.g. &ldquo;AirPods &amp; Earbuds&rdquo;) displays only products matching that subcategory and excludes unrelated products.
                </p>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Collection Name / Product Keyword
                </label>
                <input
                  type="text"
                  value={form.target}
                  onChange={(e) => selectCollection(e.target.value)}
                  placeholder="e.g. AirPods, Apple, Smartwatches, MacBook, etc."
                  className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/15 dark:bg-slate-900 dark:text-white"
                />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-bold text-slate-500">Suggested Collections:</span>
                  {["AirPods", "Apple", "Smartwatches", "Laptops", "Earbuds", "Headphones"].map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => selectCollection(sug)}
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-all",
                        form.target.toLowerCase() === sug.toLowerCase()
                          ? "border-purple-600 bg-purple-50 text-purple-700 font-bold dark:bg-purple-950 dark:text-purple-300"
                          : "border-slate-300 bg-white text-slate-600 hover:border-purple-400 hover:text-purple-600 dark:border-white/15 dark:bg-slate-900 dark:text-slate-300"
                      )}
                    >
                      {sug}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Clicking this banner navigates to products matching &ldquo;{form.target || "..."}&rdquo; across titles, tags, and variants (e.g. all AirPods models, colors, and SKUs).
                </p>
              </div>
            )}
          </div>

          {/* CTA Text */}
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-800 dark:text-slate-200">
              Button Label (CTA) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.cta}
              onChange={(e) => setField("cta", e.target.value)}
              placeholder="e.g. Shop now"
              className="h-10 w-full rounded-md border border-slate-200 bg-transparent px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
            />
            {errors.cta && <p className="mt-1 text-xs text-red-500">{errors.cta}</p>}
          </div>

          {/* Link URL (Destination URL) with Copy & Test */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-800 dark:text-slate-200">
                Destination URL <span className="text-red-500">*</span>
              </label>
              {form.link && (
                <Link
                  href={form.link}
                  target="_blank"
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#087DB5] hover:underline"
                >
                  <Eye className="h-3 w-3" />
                  Test Customer Filter
                </Link>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.link}
                onChange={(e) => setField("link", e.target.value)}
                placeholder="e.g. /search?category=Electronics or /search?collection=AirPods"
                className="h-10 flex-1 rounded-md border border-slate-200 bg-transparent px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
              />
              {form.link && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(form.link);
                    toast.success("Destination URL copied!");
                  }}
                  title="Copy destination link"
                  className="inline-flex items-center gap-1 px-3 rounded-md border border-slate-200 bg-slate-50 text-xs font-medium hover:bg-slate-100 dark:border-white/10 dark:bg-slate-800 dark:hover:bg-slate-700"
                >
                  <Copy className="h-3.5 w-3.5 text-slate-500" />
                </button>
              )}
            </div>
            {errors.link && <p className="mt-1 text-xs text-red-500">{errors.link}</p>}
          </div>
        </div>

        {/* Status Toggle & Submit */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-4 dark:border-white/10">
          <label className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-200">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField("isActive", e.target.checked)}
              className="h-4 w-4 accent-amazon-orange"
            />
            Active (Display in Storefront Carousel)
          </label>

          <div className="flex items-center gap-2">
            {editing && (
              <Button type="button" variant="outline" onClick={cancelEdit}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {editing ? "Update Banner" : "Publish Banner"}
            </Button>
          </div>
        </div>
      </form>

      {/* Promotions List Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-white/10 dark:bg-slate-900">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-white/10">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">
            Configured Promotions ({promotions.length})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Banner</th>
                <th className="px-4 py-3">Eyebrow & Title</th>
                <th className="px-4 py-3">Target Filter</th>
                <th className="px-4 py-3">Button / Link</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/10">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-slate-400" />
                    Loading promotions...
                  </td>
                </tr>
              ) : promotions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No promotional banners configured yet. Add your first banner above.
                  </td>
                </tr>
              ) : (
                promotions.map((promo, idx) => (
                  <tr key={promo.id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                    {/* Order Controls */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveOrder(promo, "up")}
                          disabled={idx === 0}
                          className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-800 disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                          title="Move Up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveOrder(promo, "down")}
                          disabled={idx === promotions.length - 1}
                          className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-800 disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                          title="Move Down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <span className="ml-1 text-xs font-bold text-slate-500">#{idx + 1}</span>
                      </div>
                    </td>

                    {/* Banner Thumbnail */}
                    <td className="px-4 py-3">
                      <div
                        className="relative shrink-0 overflow-hidden rounded-[8px] bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-white/10 shadow-2xs"
                        style={{ width: "42px", aspectRatio: "196 / 316" }}
                      >
                        <Image
                          src={promo.image}
                          alt=""
                          fill
                          sizes="50px"
                          className="object-cover"
                        />
                      </div>
                    </td>

                    {/* Eyebrow & Title */}
                    <td className="px-4 py-3">
                      <p className="text-[11px] font-bold uppercase text-amazon-orange">
                        {promo.eyebrow}
                      </p>
                      <p className="line-clamp-1 font-bold text-slate-900 dark:text-white">
                        {promo.title}
                      </p>
                      <p className="line-clamp-1 text-xs text-slate-500">{promo.description}</p>
                    </td>

                    {/* Target Filter */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 w-fit rounded-full px-2.5 py-0.5 text-xs font-bold",
                            promo.targetType === "collection"
                              ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                              : "bg-sky-100 text-[#073B70] dark:bg-sky-900/40 dark:text-sky-300 border border-sky-200 dark:border-sky-800"
                          )}
                        >
                          {promo.targetType === "collection" ? (
                            <>
                              <Layers className="h-3 w-3" />
                              Collection: {promo.target || "General"}
                            </>
                          ) : (
                            <>
                              <Tag className="h-3 w-3" />
                              Category: {promo.target || "All Categories"}
                            </>
                          )}
                        </span>
                        <Link
                          href={promo.link || "/search"}
                          target="_blank"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#087DB5] hover:underline"
                        >
                          <span>Preview Filtered Products</span>
                          <ExternalLink className="h-2.5 w-2.5" />
                        </Link>
                      </div>
                    </td>

                    {/* Button & Link */}
                    <td className="px-4 py-3">
                      <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-800 dark:bg-white/10 dark:text-slate-200">
                        {promo.cta}
                      </span>
                      <div className="mt-0.5 flex items-center gap-1">
                        <Link
                          href={promo.link}
                          target="_blank"
                          className="inline-flex items-center gap-1 text-xs text-amazon-teal hover:underline truncate max-w-[170px]"
                          title={promo.link}
                        >
                          <span className="truncate">{promo.link}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
                        </Link>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(promo)}
                        className="cursor-pointer"
                        title="Click to toggle active status"
                      >
                        {promo.isActive ? (
                          <Badge tone="success">Active</Badge>
                        ) : (
                          <Badge tone="default">Paused</Badge>
                        )}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => startEdit(promo)}
                          className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-white/10"
                          title="Edit promotion"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        {confirmDeleteId === promo.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => deletePromotion(promo.id)}
                              className="rounded bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700"
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(null)}
                              className="rounded px-2 py-1 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:text-slate-400"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(promo.id)}
                            className="rounded-md p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                            title="Delete promotion"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
